import { useEffect, useMemo, useRef, useState } from "react";
import Pause from "lucide-react/dist/esm/icons/pause";
import Play from "lucide-react/dist/esm/icons/play";
import SkipBack from "lucide-react/dist/esm/icons/skip-back";
import SkipForward from "lucide-react/dist/esm/icons/skip-forward";
import Volume1 from "lucide-react/dist/esm/icons/volume-1";
import Volume2 from "lucide-react/dist/esm/icons/volume-2";
import type { Track } from "../model";
import { createStorage } from "../services/storage";
import { useLibraryStore } from "../store/libraryStore";

const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;

export function Player({ queue }: { queue: Track[] }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const objectUrl = useRef<string | undefined>(undefined);
  const countedTrackId = useRef<string | undefined>(undefined);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [error, setError] = useState<string>();
  const currentId = useLibraryStore((state) => state.currentTrackId);
  const settings = useLibraryStore((state) => state.settings);
  const track = useMemo(() => queue.find((item) => item.id === currentId) ?? useLibraryStore.getState().tracks.find((item) => item.id === currentId), [queue, currentId]);

  useEffect(() => {
    if (!track || !audioRef.current) return;
    countedTrackId.current = undefined;
    let cancelled = false;
    setError(undefined);
    void createStorage(track.storageMode, settings.apiBaseUrl).getPlaybackUrl(track.id).then((url) => {
      if (cancelled || !audioRef.current) { URL.revokeObjectURL(url); return; }
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = url;
      audioRef.current.src = url;
      return audioRef.current.play();
    }).then(() => setPlaying(true)).catch((reason: unknown) => {
      if (reason instanceof DOMException && reason.name === "NotAllowedError") {
        setPlaying(false);
        setError("READY — PRESS PLAY");
        return;
      }
      setError(reason instanceof Error ? reason.message : "Playback failed.");
    });
    return () => { cancelled = true; };
  }, [track?.id, settings.apiBaseUrl]);

  useEffect(() => () => { if (objectUrl.current) URL.revokeObjectURL(objectUrl.current); }, []);
  useEffect(() => {
    const listener = () => toggle();
    window.addEventListener("needle-toggle-play", listener);
    return () => window.removeEventListener("needle-toggle-play", listener);
  });

  function toggle() {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      setError(undefined);
      void audio.play().catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Playback failed."));
    } else { audio.pause(); setPlaying(false); }
  }
  function move(direction: -1 | 1) {
    if (!track || queue.length === 0) return;
    const index = queue.findIndex((item) => item.id === track.id);
    const next = queue[(index + direction + queue.length) % queue.length];
    if (next) useLibraryStore.getState().setCurrentTrack(next.id);
  }

  return <footer className="player">
    <audio ref={audioRef} onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)} onDurationChange={(event) => setDuration(event.currentTarget.duration || 0)} onEnded={() => move(1)} onPlay={() => { setError(undefined); setPlaying(true); if (track && countedTrackId.current !== track.id) { countedTrackId.current = track.id; void useLibraryStore.getState().recordPlay(track.id); } }} onPause={() => setPlaying(false)} />
    <div className="transport-panel"><button aria-label="Previous track" onClick={() => move(-1)}><SkipBack size={15} fill="currentColor" /></button><button className="play-key" aria-label={playing ? "Pause" : "Play"} onClick={toggle}>{playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}</button><button aria-label="Next track" onClick={() => move(1)}><SkipForward size={15} fill="currentColor" /></button></div>
    <div className="now-playing"><span>NOW PLAYING</span><strong>{track?.title ?? "NO TRACK LOADED"}</strong><small>{error ?? (track ? `${track.artist || "Unknown artist"} — ${track.album || "Unknown album"}` : "Select a track and press Enter")}</small></div>
    <time>{clock(currentTime)}</time>
    <input className="timeline" type="range" min="0" max={duration || 0} step="0.1" value={Math.min(currentTime, duration || 0)} aria-label="Playback position" onChange={(event) => { if (audioRef.current) audioRef.current.currentTime = Number(event.target.value); }} style={{ "--progress": `${duration ? (currentTime / duration) * 100 : 0}%` } as React.CSSProperties} />
    <time>-{clock(Math.max(0, duration - currentTime))}</time>
    <div className="volume">{volume === 0 ? <Volume1 size={14} /> : <Volume2 size={14} />}<input type="range" min="0" max="1" step="0.01" value={volume} aria-label="Volume" onChange={(event) => { const next = Number(event.target.value); setVolume(next); if (audioRef.current) audioRef.current.volume = next; }} /></div>
    <div className="player-led"><i className={playing ? "on" : ""} />{playing ? "PLAY" : "STOP"}</div>
  </footer>;
}
